import { Head, useForm } from "@inertiajs/react";
import { AlertCircle } from "lucide-react";
import type { FormEventHandler } from "react";
import Dropzone from "shadcn-dropzone";

import InputError from "@/components/input-error";
import PurchaseRequestPickerWithPreview from "@/components/purchase-request-picker-with-preview";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { FilePreview } from "@/components/ui/file-preview";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { TextareaWithCounter } from "@/components/ui/textarea-with-counter";
import { ACCEPTED_FILE_TYPES } from "@/constants/file";
import { useOrganizationRoutes } from "@/hooks/use-organization-routes";
import AppLayout from "@/layouts/app-layout";
import type { BreadcrumbItem } from "@/types";

export default function UploadNew() {
  const routes = useOrganizationRoutes();

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: "Facturas",
      href: routes.invoices.index(),
    },
    {
      title: "Cargar Factura",
      href: routes.invoices.uploadNew(),
    },
  ];
  const {
    data,
    setData,
    post,
    processing,
    progress,
    errors,
    transform,
  } = useForm({
    observations: "",
    invoice_files: [] as File[],
    purchase_request_id: null as number | null,
  });

  const submit: FormEventHandler = (e) => {
    e.preventDefault();

    if (data.invoice_files.length === 0) {
      return;
    }

    transform((formState) => {
      const formData = new FormData();
      formData.append("observations", formState.observations);

      if (formState.purchase_request_id !== null) {
        formData.append("purchase_request_id", String(formState.purchase_request_id));
      }

      formState.invoice_files.forEach((file) => {
        formData.append("invoice_files[]", file);
      });

      return formData;
    });

    post(routes.invoices.index());
  };

  const handleInvoiceFilesChange = (acceptedFiles: File[]) => {
    // Agregar archivos en vez de reemplazar
    setData("invoice_files", [...data.invoice_files, ...acceptedFiles]);
  };

  const removeInvoiceFile = (index: number) => {
    setData(
      "invoice_files",
      data.invoice_files.filter((_, i) => i !== index),
    );
  };

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Cargar Factura" />
      <div className="flex h-full flex-1 flex-col p-4 md:p-6">
        <div className="w-full max-w-2xl mx-auto">
          <Alert className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Carga rápida de factura</AlertTitle>
            <AlertDescription>
              Sube todas las páginas de la factura y la IA extraerá automáticamente los
              ítems, precios y totales. Podrás revisar y confirmar antes de
              guardar.
            </AlertDescription>
          </Alert>

          <Card className="shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle>Nueva Factura</CardTitle>
              <CardDescription>
                Adjunta los archivos de la factura y agrega observaciones si lo
                deseas
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              <form className="flex flex-col gap-4" onSubmit={submit}>
                {/* Solicitud de Compra (opcional) */}
                <div className="grid gap-2">
                  <Label htmlFor="purchase_request_id">Solicitud de Compra (opcional)</Label>
                  <PurchaseRequestPickerWithPreview
                    value={data.purchase_request_id}
                    onChange={(value) => setData("purchase_request_id", value)}
                    disabled={processing}
                    placeholder="Buscar por número o sector..."
                  />
                  <InputError message={errors.purchase_request_id} />
                </div>

                {/* Observaciones */}
                <div className="grid gap-2">
                  <Label htmlFor="observations">Observaciones</Label>
                  <TextareaWithCounter
                    id="observations"
                    value={data.observations}
                    onChange={(e) => setData("observations", e.target.value)}
                    disabled={processing}
                    rows={3}
                    placeholder="Agrega notas u observaciones sobre esta factura..."
                    maxLength={500}
                    showCharacterCount={true}
                  />
                  <InputError message={errors.observations} />
                </div>

                {/* Archivos de Factura */}
                <div className="grid gap-2">
                  <Label>Factura (todas las páginas) *</Label>
                  <Dropzone
                    onDrop={handleInvoiceFilesChange}
                    accept={ACCEPTED_FILE_TYPES.PDF_AND_IMAGES}
                    maxFiles={10}
                    disabled={processing}
                    containerClassName="w-full"
                    dropZoneClassName="w-full h-32 rounded-lg border-2 border-dashed border-border hover:border-primary/50 transition-colors flex items-center justify-center cursor-pointer"
                  >
                    {(dz) => (
                      <div className="text-center p-4">
                        <p className="text-sm text-muted-foreground">
                          {dz.isDragActive
                            ? "Suelta las páginas aquí"
                            : "Arrastra todas las páginas de la factura o haz clic"}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          PDF, JPG, PNG · Si la factura tiene varias páginas, subí todas (hasta 10 archivos)
                        </p>
                      </div>
                    )}
                  </Dropzone>

                  {data.invoice_files.length > 0 && (
                    <div className="grid gap-2 mt-2">
                      {data.invoice_files.map((file, index) => (
                        <FilePreview
                          key={index}
                          file={file}
                          onRemove={() => removeInvoiceFile(index)}
                          disabled={processing}
                        />
                      ))}
                    </div>
                  )}
                  <InputError message={errors.invoice_files} />
                </div>

                {/* Botones */}
                <div className="flex justify-end gap-2 pt-4">
                  {progress && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground pr-2">
                      <Spinner className="size-4" />
                      <span>
                        Subiendo archivos…
                      </span>
                    </div>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => window.history.back()}
                    disabled={processing}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={data.invoice_files.length === 0 || processing}
                  >
                    {processing ? (
                      <span className="flex items-center gap-2">
                        <Spinner className="size-4" />
                        Procesando…
                      </span>
                    ) : (
                      "Guardar y Procesar"
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}
